import Card from 'components/card';
import InputTagField from 'components/fields/InputTagField';
import CustomerSearchBar from 'components/fields/CustomerSearchBar';
import { useEffect, useState } from 'react';
import { FiCheck, FiEdit2, FiPackage, FiPlus, FiUsers, FiX } from 'react-icons/fi';
import { IoMdTrash } from 'react-icons/io';
import {
    createPricing,
    deletePricing,
    getAllPricings,
    updateSpecificPricing,
} from 'interfaces/pricing';
import { getCustomersForMultiSelect, searchCustomersForSelect } from 'interfaces/customer';
import { getMyInfo, updateMyInfo } from 'interfaces/user';
import { toast } from 'react-toastify';

interface CustomPricingWidgetProps {
    productId: string;
    basePrice?: number;
    purchasePrice?: number;
    packagings?: any[];
}

const CustomPricingWidget = ({ productId, basePrice, purchasePrice, packagings }: CustomPricingWidgetProps) => {
    const [loading, setLoading] = useState(true);
    const [pricings, setPricings] = useState<any[]>([]);
    const [customersMap, setCustomersMap] = useState<Record<string, string>>({});
    const [userTags, setUserTags] = useState<string[]>([]);

    const [showAddForm, setShowAddForm] = useState(false);
    const [newMode, setNewMode] = useState<'tag' | 'client'>('tag');
    const [newTags, setNewTags] = useState<string[]>([]);
    const [newCustomer, setNewCustomer] = useState<any>(null);
    const [customerResults, setCustomerResults] = useState<any[]>([]);
    const [newPackagingIdx, setNewPackagingIdx] = useState<string>('');
    const [newPrice, setNewPrice] = useState<number>(0);
    const [saving, setSaving] = useState(false);

    const [editingId, setEditingId] = useState<string | null>(null);
    const [tempPrice, setTempPrice] = useState<number>(0);

    const loadPricings = async () => {
        if (!productId) return;
        setLoading(true);
        const res = await getAllPricings([productId]);
        if (res) setPricings(res.data || []);
        setLoading(false);
    };

    const loadCustomersMap = async () => {
        const list = await getCustomersForMultiSelect();
        if (Array.isArray(list) && list.length > 0) {
            const map: Record<string, string> = {};
            list.forEach((c: any) => {
                map[c._id] = c.displayName || c.company || 'Client';
            });
            setCustomersMap(map);
        }
    };

    const loadUser = async () => {
        const res = await getMyInfo();
        if (res?.data) setUserTags(res.data.tags || []);
    };

    useEffect(() => {
        loadPricings();
        loadCustomersMap();
        loadUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    const addNewTag = async (tag: string) => {
        const updatedTags = [...userTags, tag];
        setUserTags(updatedTags);
        await updateMyInfo({ tags: updatedTags });
    };

    const handleCustomerSearch = async (value: string) => {
        if (value.trim().length < 2) {
            setCustomerResults([]);
            return;
        }
        const list = await searchCustomersForSelect(value);
        setCustomerResults(list || []);
    };

    const resetAddForm = () => {
        setShowAddForm(false);
        setNewMode('tag');
        setNewTags([]);
        setNewCustomer(null);
        setCustomerResults([]);
        setNewPackagingIdx('');
        setNewPrice(basePrice || 0);
    };

    const handleCreate = async () => {
        if (newMode === 'tag' && newTags.length === 0) {
            toast.error('Ajoutez au moins un tag');
            return;
        }
        if (newMode === 'client' && !newCustomer) {
            toast.error('Sélectionnez un client');
            return;
        }

        const data: any = { productId, price: newPrice };
        if (newMode === 'tag') data.tags = newTags;
        else data.customerId = newCustomer._id;

        if (newPackagingIdx !== '' && packagings?.[parseInt(newPackagingIdx, 10)]) {
            const pkg = packagings[parseInt(newPackagingIdx, 10)];
            data.packagingRef = pkg.ref || '';
            data.packagingUnity = pkg.unity;
            data.packagingWeight = pkg.weight;
        }

        setSaving(true);
        const res = await createPricing(data);
        setSaving(false);

        if (res) {
            setPricings((prev) => [...prev, res]);
            if (newMode === 'client' && newCustomer) {
                setCustomersMap((prev) => ({
                    ...prev,
                    [newCustomer._id]: newCustomer.displayName || newCustomer.company,
                }));
            }
            resetAddForm();
            toast.success('Prix personnalisé ajouté');
        } else {
            toast.error("Erreur lors de la création du prix personnalisé");
        }
    };

    const handleEditPrice = (id: string, price: number) => {
        setEditingId(id);
        setTempPrice(price);
    };

    const handleSavePrice = async (pricing: any) => {
        const res = await updateSpecificPricing(pricing._id, {
            price: tempPrice,
            productId: pricing.productId,
            tags: pricing.tags,
        });
        if (res) {
            setPricings((prev) =>
                prev.map((p) => (p._id === pricing._id ? { ...p, price: tempPrice } : p)),
            );
        }
        setEditingId(null);
    };

    const handleDelete = async (id: string) => {
        const res = await deletePricing(id);
        if (res) setPricings((prev) => prev.filter((p) => p._id !== id));
    };

    const getMarginString = (price: number) => {
        if (!purchasePrice || !price || isNaN(price) || price <= 0) return null;
        const margin = price - purchasePrice;
        const marginPercent = (margin / price) * 100;
        return `Marge : ${margin.toFixed(2)} € (${marginPercent.toFixed(1)} %)`;
    };

    if (loading) {
        return (
            <Card extra="col-span-12 px-5 py-5 border">
                <h4 className="mb-4 text-lg font-medium">Prix personnalisés</h4>
                <div className="flex justify-center items-center py-10">
                    <p className="text-gray-500">Chargement...</p>
                </div>
            </Card>
        );
    }

    return (
        <Card extra="col-span-12 px-5 py-5 border">
            <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                    <h4 className="text-lg font-medium">Prix personnalisés</h4>
                    <p className="mt-1 text-xs text-gray-500">
                        Tarifs par tag client ou par client, appliqués à la place du prix de vente
                        ({Number(basePrice || 0).toFixed(2)} €).
                    </p>
                </div>
                {!showAddForm && (
                    <button
                        onClick={() => {
                            setNewPrice(basePrice || 0);
                            setShowAddForm(true);
                        }}
                        className="flex shrink-0 items-center gap-1 px-3 py-1.5 text-xs border border-[#01499b] text-[#01499b] rounded-lg hover:bg-blue-50 transition"
                    >
                        <FiPlus size={14} /> Ajouter un prix
                    </button>
                )}
            </div>

            {showAddForm && (
                <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="flex gap-2 mb-3">
                        <button
                            onClick={() => setNewMode('tag')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                                newMode === 'tag'
                                    ? 'bg-[#01499b] text-white border-[#01499b]'
                                    : 'text-[#01499b] border-gray-200 hover:bg-blue-50'
                            }`}
                        >
                            Par tag client
                        </button>
                        <button
                            onClick={() => setNewMode('client')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                                newMode === 'client'
                                    ? 'bg-[#01499b] text-white border-[#01499b]'
                                    : 'text-[#01499b] border-gray-200 hover:bg-blue-50'
                            }`}
                        >
                            Par client
                        </button>
                    </div>

                    {newMode === 'tag' ? (
                        <InputTagField
                            label="Tags clients"
                            placeholder="Ajouter un tag puis Entrée"
                            id="new-pricing-tags"
                            type="text"
                            extra=""
                            tags={newTags}
                            allTags={userTags.map((name) => ({ name }))}
                            onTagCreate={(tag: string) => addNewTag(tag)}
                            onChange={(tags: string[]) => setNewTags(tags)}
                        />
                    ) : (
                        <div>
                            <CustomerSearchBar
                                label="Client"
                                displayKey="company"
                                options={customerResults}
                                onSelect={(c: any) => {
                                    setNewCustomer(c);
                                    setCustomerResults([]);
                                }}
                                onSearch={handleCustomerSearch}
                                inputSmall={true}
                            />
                            {newCustomer && (
                                <div className="mt-1 flex items-center gap-2 text-sm">
                                    <FiUsers className="text-[#01499b]" size={14} />
                                    <span className="font-medium">
                                        {newCustomer.displayName || newCustomer.company}
                                    </span>
                                    <button
                                        onClick={() => setNewCustomer(null)}
                                        className="text-gray-400 hover:text-red-500"
                                    >
                                        <FiX size={14} />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {packagings && packagings.length > 0 && (
                        <div className="mt-3">
                            <label className="text-xs text-gray-500">Conditionnement (optionnel)</label>
                            <select
                                className="mt-1 w-full h-[36px] text-sm rounded-lg border border-gray-300 px-2 outline-none focus:border-toola-500 bg-white"
                                value={newPackagingIdx}
                                onChange={(e) => setNewPackagingIdx(e.target.value)}
                            >
                                <option value="">Produit de base</option>
                                {packagings.map((pkg: any, idx: number) => (
                                    <option key={idx} value={idx}>
                                        {pkg.unity} ({pkg.weight}) - {pkg.price} €
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="mt-3 flex items-end gap-3">
                        <div>
                            <label className="text-xs text-gray-500">Prix personnalisé</label>
                            <div className="mt-1 flex items-center gap-1">
                                <input
                                    type="number"
                                    step="0.01"
                                    value={newPrice}
                                    onChange={(e) => setNewPrice(parseFloat(e.target.value) || 0)}
                                    className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#01499b] focus:ring-opacity-40 focus:border-[#01499b]"
                                />
                                <span className="text-sm text-gray-600">€</span>
                            </div>
                        </div>
                        <button
                            onClick={handleCreate}
                            disabled={saving}
                            className="flex items-center gap-1 px-3 py-2 text-xs bg-[#01499b] text-white rounded-lg hover:bg-[#013875] transition disabled:opacity-50"
                        >
                            {saving ? '...' : <><FiCheck size={14} /> Enregistrer</>}
                        </button>
                        <button
                            onClick={resetAddForm}
                            className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                        >
                            Annuler
                        </button>
                    </div>
                </div>
            )}

            {pricings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                    <p className="text-gray-500">Aucun prix personnalisé pour ce produit</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {pricings.map((pricing: any) => (
                        <div
                            key={pricing._id}
                            className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded hover:border-[#01499b] hover:bg-blue-50/30 transition"
                        >
                            <div className="flex flex-1 items-center gap-2 text-sm text-gray-700">
                                {pricing.customerId ? (
                                    <>
                                        <FiUsers className="text-[#01499b]" size={14} />
                                        <span className="text-gray-500">Client:</span>
                                        <span className="font-medium">
                                            {customersMap[pricing.customerId] || 'Client inconnu'}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <FiPackage className="text-[#01499b]" size={14} />
                                        <span className="text-gray-500">Tags:</span>
                                        <span className="font-medium">
                                            {pricing.tags?.join(', ') || '-'}
                                        </span>
                                    </>
                                )}
                                {pricing.packagingUnity && (
                                    <span className="ml-1 text-xs text-gray-500">
                                        • {pricing.packagingUnity}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-col items-end">
                                {editingId === pricing._id ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={tempPrice}
                                            onChange={(e) => setTempPrice(parseFloat(e.target.value) || 0)}
                                            className="w-20 px-2 py-1 text-sm border border-[#01499b] rounded focus:outline-none focus:ring-2 focus:ring-[#01499b] focus:ring-opacity-50"
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleSavePrice(pricing);
                                                else if (e.key === 'Escape') setEditingId(null);
                                            }}
                                        />
                                        <span className="text-sm text-gray-600">€</span>
                                        <button
                                            onClick={() => handleSavePrice(pricing)}
                                            className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                                            title="Enregistrer"
                                        >
                                            <FiCheck size={18} />
                                        </button>
                                        <button
                                            onClick={() => setEditingId(null)}
                                            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"
                                            title="Annuler"
                                        >
                                            <FiX size={18} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <p className="min-w-[60px] text-right text-sm font-semibold text-[#01499b]">
                                            {pricing.price} €
                                        </p>
                                        <button
                                            onClick={() => handleEditPrice(pricing._id, pricing.price)}
                                            className="p-1 text-[#01499b] hover:text-[#013875] hover:bg-blue-50 rounded transition"
                                            title="Modifier le prix"
                                        >
                                            <FiEdit2 size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(pricing._id)}
                                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition"
                                            title="Supprimer"
                                        >
                                            <IoMdTrash size={18} />
                                        </button>
                                    </div>
                                )}
                                {(() => {
                                    const currentPrice = editingId === pricing._id ? tempPrice : pricing.price;
                                    const marginStr = getMarginString(currentPrice);
                                    if (!marginStr) return null;
                                    return <p className="mt-1 text-xs text-gray-500">{marginStr}</p>;
                                })()}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
};

export default CustomPricingWidget;
